import {
  InvalidInputError,
  UnknownApiError,
} from './errors';
import {
  assertAuthData,
  assertString,
  assertRequiredString,
  assertRequiredNonEmptyObject,
  assertAuthorized,
} from './utils';

export default function (api) {
  /**
   * @memberof API
   * @instance
   */
  const parentalControl = {
    /**
     * Retrieve the current youth-protection state for an asset.
     * Auth token is required.
     *
     * @method
     *
     * @param {object} payload non-empty object
     * @param {string} payload.assetId
     * @param {string} [payload.iso8601Time] like `2017-06-12T09:37:45+01:00`
     *
     * @return {Promise} promise
     * @fulfill {boolean} true if pin code is required, false otherwise
     * @reject {AuthTokenNotSetError}
     * @reject {ArgumentError}
     * @reject {UnauthorizedError}
     * @reject {InvalidInputError}
     * @reject {UnknownApiError} Server returned with unexpected status code
    */
    async isProtectedAsset(payload) {
      assertAuthData(api.authData);
      assertRequiredNonEmptyObject(payload, 'payload');
      assertRequiredString(payload.assetId, 'payload.assetId');
      assertString(payload.iso8601Time, 'payload.iso8601Time');

      const data = {
        userId: api.authData.userId,
        assetId: payload.assetId,
        iso8601Time: payload.iso8601Time,
      };
      const res = await api.request('post', '/parental-control/v1/asset', { data });
      assertAuthorized(res);

      switch (res.status) {
        case 200: {
          return res.body.pinCodeRequired;
        }
        case 400: {
          throw new InvalidInputError(res.body.message);
        }
        default: {
          throw new UnknownApiError('server returned with unhandled status code', res);
        }
      }
    },

    /**
    * @typedef {Object} ParentalControlStatusResponse
    * @property {boolean} active If parental control is active or inactive.
    * @property {boolean} hasPinCode If the user has a pincode set.
    */

    /**
     * Retrieve the current parental control state.
     * Auth token is required.
     *
     * @method
     *
     * @return {Promise} promise
     * @fulfill {ParentalControlStatusResponse}
     * @reject {AuthTokenNotSetError}
     * @reject {ArgumentError}
     * @reject {UnauthorizedError}
     * @reject {UnknownApiError} Server returned with unexpected status code
    */
    async getStatus() {
      assertAuthData(api.authData);

      const res = await api.request(
        'get',
        `/parental-control/v1/parental-control/${api.authData.userId}`,
      );
      assertAuthorized(res);

      switch (res.status) {
        case 200: {
          return res.body;
        }
        default: {
          throw new UnknownApiError('server returned with unhandled status code', res);
        }
      }
    },

    /**
     * Modify parental control state.
     * Auth token is required.
     *
     * @method
     *
     * @param {object} payload non-empty object
     * @param {string} [payload.newCode] Desired new code, if any
     * @param {boolean} [payload.active] Desired state for parental control
     * @param {string} [payload.oldCode] The current parental-control code if the user has one
     *
     * @return {Promise} promise
     * @fulfill {undefined}
     * @reject {AuthTokenNotSetError}
     * @reject {ArgumentError}
     * @reject {UnauthorizedError}
     * @reject {InvalidInputError} Invalid oldCode in request content
     * @reject {UnknownApiError} Server returned with unexpected status code
    */
    async modify(payload) {
      assertAuthData(api.authData);
      assertRequiredNonEmptyObject(payload, 'payload');
      assertString(payload.newCode, 'payload.newCode');
      assertString(payload.active, 'payload.active');
      assertString(payload.oldCode, 'payload.oldCode');

      const res = await api.request(
        'patch',
        `/parental-control/v1/parental-control/${api.authData.userId}`,
        { data: payload },
      );
      assertAuthorized(res);

      switch (res.status) {
        case 202: {
          return undefined;
        }
        case 403: {
          throw new InvalidInputError(res.body.message);
        }
        default: {
          throw new UnknownApiError('server returned with unhandled status code', res);
        }
      }
    },

    /**
     * Verify parental control code.
     * Auth token is required.
     *
     * @method
     *
     * @param {object} payload non-empty object
     * @param {string} payload.code the pincode to verify
     *
     * @return {Promise} promise
     * @fulfill {boolean} true if pincode is valid, false otherwise
     * @reject {AuthTokenNotSetError}
     * @reject {ArgumentError}
     * @reject {UnauthorizedError}
     * @reject {UnknownApiError} Server returned with unexpected status code
    */
    async verifyCode(payload) {
      assertAuthData(api.authData);
      assertRequiredNonEmptyObject(payload, 'payload');
      assertRequiredString(payload.code, 'payload.code');

      const res = await api.request(
        'get',
        `/parental-control/v1/parental-control/${api.authData.userId}/code/${payload.code}`,
      );
      assertAuthorized(res);

      switch (res.status) {
        case 200: {
          return true;
        }
        case 403: {
          return false;
        }
        default: {
          throw new UnknownApiError('server returned with unhandled status code', res);
        }
      }
    },

    /**
     * Reset parental control code.
     * A new code will be genereated and e-mailed to the user, and parental control will be active.
     * Auth token is required.
     *
     * @method
     *
     * @return {Promise} promise
     * @fulfill {undefined}
     * @reject {AuthTokenNotSetError}
     * @reject {ArgumentError}
     * @reject {UnauthorizedError}
     * @reject {InvalidInputError} Invalid pincode
     * @reject {UnknownApiError} Server returned with unexpected status code
    */
    async resetCode() {
      assertAuthData(api.authData);

      const res = await api.request(
        'post',
        `/parental-control/v1/parental-control/${api.authData.userId}/reset`,
      );
      assertAuthorized(res);

      switch (res.status) {
        case 202: {
          return undefined;
        }
        default: {
          throw new UnknownApiError('server returned with unhandled status code', res);
        }
      }
    },
  };

  return parentalControl;
}
